import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

// ---------------------------------------------------------------------------
// Sala de voz/video em MALHA (mesh): cada participante mantem uma conexao P2P
// direta com cada um dos outros. Nao ha servidor de midia — o backend so
// encaminha offer/answer/ICE pelo WebSocket /ws/sala.
//
// Custo da malha: com N pessoas cada navegador faz N-1 uploads e N-1
// downloads. Por isso o backend trava a sala em 5 e este hook derruba a
// resolucao quando a sala passa de `limiteVideoAlta`. Acima disso o caminho
// certo e um SFU, que e outra arquitetura.
// ---------------------------------------------------------------------------

// STUN publico sempre; TURN opcional via env vars. Pares dentro da mesma LAN
// fecham por candidatos `host` e nao usam nem o STUN. Pares externos (home
// office, celular, VPN) frequentemente estao atras de NAT simetrico — nesse
// caso STUN nao basta e a conexao trava em `failed` sem um TURN para relaymar
// o RTP. Configurar `REACT_APP_TURN_URL` (uma ou mais URLs separadas por
// virgula) + credenciais habilita o relay. O chip de rota no tile mostra
// "via TURN" quando o par fechou no relay.
function montarIceServers() {
  const servidores = [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
  ];
  const turnUrl = (process.env.REACT_APP_TURN_URL || '').trim();
  const turnUser = process.env.REACT_APP_TURN_USERNAME;
  const turnCred = process.env.REACT_APP_TURN_CREDENTIAL;
  if (turnUrl && turnUser && turnCred) {
    servidores.push({
      urls: turnUrl.split(',').map((s) => s.trim()).filter(Boolean),
      username: turnUser,
      credential: turnCred,
    });
  }
  return servidores;
}

const ICE_SERVERS = montarIceServers();

// Papeis das m-lines, NA ORDEM em que sao criadas. Os dois lados usam esta
// mesma ordem, entao o indice do transceiver identifica o papel sem precisar
// de negociacao extra.
//
// A m-line de tela existe desde o inicio, mesmo sem ninguem compartilhando:
// e ela que permite mandar camera E tela AO MESMO TEMPO (sua camera nao some
// quando voce apresenta) sem nunca renegociar. Enquanto ninguem compartilha o
// sender fica sem faixa e nao trafega nada.
const PAPEIS = ['audio', 'camera', 'tela'];

const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

// Pedimos 720p. NAO e so qualidade: varias webcams (Logitech em especial)
// implementam resolucoes baixas RECORTANDO o sensor em vez de reduzir a
// imagem — pedir 640x360 de uma camera 1080p sai com cara de zoom, com menos
// campo de visao. Pedir a resolucao nativa evita esse recorte.
//
// A proporcao 16:9 e pedida de proposito: numa webcam 4:3 o navegador corta em
// cima/embaixo, preservando a largura da cena. Sem pedir, viria 4:3 e o tile
// (16:9) cortaria as LATERAIS — ai sim viraria zoom horizontal.
const VIDEO_ALTA = {
  width:     { ideal: 1280 },
  height:    { ideal: 720 },
  frameRate: { ideal: 30, max: 30 },
};

const VIDEO_BAIXA = {
  width:     { ideal: 640 },
  height:    { ideal: 360 },
  frameRate: { ideal: 24, max: 30 },
};

const BITRATE_ALTA  = 1500000;
const BITRATE_BAIXA = 600000;
// A tela ganha mais banda que a camera: e nela que tem texto para ler. Mas
// tambem cede quando a sala enche — sao N-1 uploads simultaneos.
const BITRATE_TELA_ALTA  = 2500000;
const BITRATE_TELA_BAIXA = 1200000;

// Espera o ICE assentar antes de ler o par escolhido — logo apos o
// `connected` o par nominado ainda pode trocar.
const DELAY_STATS_MS = 1500;
// Quanto esperar, depois de a conexao ficar pronta, para conferir se o audio
// realmente saiu do lugar. Ver `vigiarAudio`.
const VIGIA_AUDIO_MS = 5000;
const JANELA_VIGIA_MS = 2500;

// Um par ICE pode cair sem o outro sair da sala: Wi-Fi oscila, hotspot troca
// de celula, VPN pisca. `disconnected` costuma se recuperar sozinho em 1-2s;
// se persistir, disparamos um restart ICE. Somente o ofertante manda a nova
// offer — mantem a regra unilateral do modulo e evita glare.
const ICE_RECUPERACAO_MS = 4000;
const ICE_MAX_TENTATIVAS_RESTART = 3;

function wsUrl(token) {
  const base = API.replace(/^http/i, 'ws').replace(/\/+$/, '');
  return `${base}/ws/sala?token=${encodeURIComponent(token)}`;
}

function participanteBase(p) {
  return {
    peerId:             p.peerId,
    usuarioId:          p.usuarioId,
    nome:               p.nome,
    entrouEm:           p.entrouEm,
    mudo:               !!p.mudo,
    cameraDesligada:    !!p.cameraDesligada,
    compartilhandoTela: !!p.compartilhandoTela,
    stream:             null,  // audio + camera
    streamTela:         null,  // tela compartilhada, quando houver
    conexao:            'new',
    rota:               null,
  };
}

export default function useSalaWebRTC({ limiteVideoAlta = 3 } = {}) {
  const [status, setStatus] = useState('ocioso'); // ocioso | conectando | na-sala | erro
  const [sala, setSala] = useState(null);
  const [trocandoPara, setTrocandoPara] = useState(null);
  const [meuPeerId, setMeuPeerId] = useState(null);
  const [participantes, setParticipantes] = useState([]);
  const [streamLocal, setStreamLocal] = useState(null);
  const [streamTela, setStreamTela] = useState(null);
  const [mudo, setMudo] = useState(false);
  const [cameraDesligada, setCameraDesligada] = useState(false);
  const [compartilhandoTela, setCompartilhandoTela] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  // O que a camera esta REALMENTE entregando (getSettings da faixa), nao o
  // que pedimos. E o dado para responder "esta com zoom?" / "que resolucao?".
  const [configCamera, setConfigCamera] = useState(null);
  const [dispositivos, setDispositivos] = useState({ microfones: [], cameras: [], saidas: [] });
  const [saidaAudio, setSaidaAudio] = useState('');
  const [mensagens, setMensagens] = useState([]);
  const [chatCarregando, setChatCarregando] = useState(false);

  const wsRef = useRef(null);
  // peerId -> { pc, senders: {audio,camera,tela}, streams: {camera,tela},
  //             filaCandidates, statsTimer, vigiaTimer, vigiaEspera }
  const peersRef = useRef(new Map());
  const streamLocalRef = useRef(null);
  const streamTelaRef = useRef(null);
  const cameraTrackRef = useRef(null);
  const saindoRef = useRef(false);
  // Marca que a queda foi por outra aba ter assumido, para o onclose nao
  // sobrescrever com o generico "conexao perdida".
  const substituidoRef = useRef(false);
  // Perfil de video vigente, para uma troca de camera nascer ja na qualidade
  // certa para o tamanho atual da sala.
  const perfilVideoRef = useRef(VIDEO_ALTA);
  const mudoRef = useRef(false);
  const cameraOffRef = useRef(false);

  const enviar = useCallback((mensagem) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(mensagem));
    }
  }, []);

  const atualizarParticipante = useCallback((peerId, patch) => {
    setParticipantes((prev) => prev.map((p) => (p.peerId === peerId ? { ...p, ...patch } : p)));
  }, []);

  // --- Qualidade -----------------------------------------------------------
  // Malha com 4+ pessoas: cai para 320x180 e limita o bitrate de cada sender
  // de camera. A tela tem orcamento proprio e NAO perde resolucao — texto
  // ilegivel nao serve para nada.
  const aplicarQualidade = useCallback(async (totalNaSala) => {
    const alta = totalNaSala <= limiteVideoAlta;
    const perfil = alta ? VIDEO_ALTA : VIDEO_BAIXA;
    perfilVideoRef.current = perfil;

    const camera = cameraTrackRef.current;
    if (camera) {
      try {
        await camera.applyConstraints(perfil);
      } catch (_) { /* camera que nao aceita a constraint: segue com o que da */ }
      // Le o que a camera de fato entregou — pode ser diferente do pedido.
      try { setConfigCamera(camera.getSettings()); } catch (_) { /* ignora */ }
    }

    const limitar = async (sender, maxBitrate) => {
      if (!sender) return;
      try {
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
        params.encodings[0].maxBitrate = maxBitrate;
        await sender.setParameters(params);
      } catch (_) { /* setParameters antes da negociacao: ignora */ }
    };

    for (const entry of peersRef.current.values()) {
      await limitar(entry.senders.camera, alta ? BITRATE_ALTA : BITRATE_BAIXA);
      await limitar(entry.senders.tela, alta ? BITRATE_TELA_ALTA : BITRATE_TELA_BAIXA);
    }
  }, [limiteVideoAlta]);

  // Lista os dispositivos disponiveis. So devolve rotulos ("Logitech C920")
  // DEPOIS que o usuario concedeu permissao de midia — antes disso os nomes
  // vem vazios, por privacidade. Por isso so chamamos apos o getUserMedia.
  const listarDispositivos = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const todos = await navigator.mediaDevices.enumerateDevices();
      const porTipo = (kind) => todos
        .filter((d) => d.kind === kind && d.deviceId)
        .map((d, i) => ({ id: d.deviceId, nome: d.label || `${kind} ${i + 1}` }));
      setDispositivos({
        microfones: porTipo('audioinput'),
        cameras:    porTipo('videoinput'),
        // audiooutput so existe no Chrome/Edge. No Firefox a lista vem vazia e
        // a UI esconde o seletor de saida.
        saidas:     porTipo('audiooutput'),
      });
    } catch (_) { /* lista de dispositivos e nice-to-have */ }
  }, []);

  // --- Diagnostico de rota ICE --------------------------------------------
  const medirRota = useCallback(async (peerId, pc) => {
    try {
      const stats = await pc.getStats();
      let par = null;
      stats.forEach((r) => {
        if (r.type === 'candidate-pair' && r.state === 'succeeded' && (r.nominated || !par)) {
          par = r;
        }
      });
      if (!par) return;
      const local = stats.get(par.localCandidateId);
      const remoto = stats.get(par.remoteCandidateId);
      atualizarParticipante(peerId, {
        rota: { local: local?.candidateType || null, remoto: remoto?.candidateType || null },
      });
    } catch (_) { /* diagnostico e nice-to-have */ }
  }, [atualizarParticipante]);

  // --- Peer connections ----------------------------------------------------
  const fecharPeer = useCallback((peerId) => {
    const entry = peersRef.current.get(peerId);
    if (!entry) return;
    clearTimeout(entry.statsTimer);
    clearTimeout(entry.vigiaTimer);
    clearTimeout(entry.vigiaEspera);
    clearTimeout(entry.recuperaTimer);
    try { entry.pc.close(); } catch (_) { /* ja fechado */ }
    peersRef.current.delete(peerId);
  }, []);

  // Reinicia o ICE de um peer que caiu em `failed` ou ficou muito tempo em
  // `disconnected`. So dispara do lado ofertante — o respondente ja recebe a
  // nova offer pelo caminho normal (tratarSinal) e responde com nova answer.
  // Se o restart nao voltar a `connected` em algumas tentativas, para: a
  // seguir e o participante-saiu do servidor que vai limpar o peer.
  const reiniciarIce = useCallback(async (peerId) => {
    const entry = peersRef.current.get(peerId);
    if (!entry || !entry.ofertante || entry.iceRestartEmAndamento) return;
    if (entry.tentativasRestart >= ICE_MAX_TENTATIVAS_RESTART) return;

    entry.iceRestartEmAndamento = true;
    entry.tentativasRestart += 1;
    try {
      const offer = await entry.pc.createOffer({ iceRestart: true });
      await entry.pc.setLocalDescription(offer);
      enviar({ tipo: 'sinal', para: peerId, payload: { tipo: 'offer', sdp: entry.pc.localDescription } });
      console.warn(`[sala] restart ICE disparado para ${peerId} (tentativa ${entry.tentativasRestart})`);
    } catch (err) {
      console.error('[sala] falha ao reiniciar ICE:', err);
    } finally {
      entry.iceRestartEmAndamento = false;
    }
  }, [enviar]);

  // Aponta os senders da conexao para as faixas locais e forca as m-lines a
  // `sendrecv`.
  //
  // QUANDO chamar depende do papel, e essa distincao e a parte delicada:
  //
  // - Ofertante: logo apos criar os transceivers na mao.
  // - Respondente: SO DEPOIS do setRemoteDescription(offer). O Chrome NAO
  //   reaproveita transceivers criados por addTransceiver para casar com uma
  //   offer que chega — ele cria outros, `recvonly`, e os nossos ficam orfaos
  //   (currentDirection null, nunca negociados). O sintoma engana: o ICE
  //   conecta, o chip de rota acende, e so a midia de quem respondeu nao sai.
  const aplicarMidiaLocal = useCallback((entry) => {
    const local = streamLocalRef.current;
    const faixa = {
      audio:  local?.getAudioTracks()[0] || null,
      camera: cameraTrackRef.current || null,
      tela:   streamTelaRef.current?.getVideoTracks()[0] || null,
    };

    entry.pc.getTransceivers().forEach((transceiver, indice) => {
      const papel = PAPEIS[indice];
      if (!papel || entry.senders[papel]) return;

      transceiver.direction = 'sendrecv';
      entry.senders[papel] = transceiver.sender;

      // O sender fica marcado como tratado mesmo sem faixa — e este metodo nao
      // reprocessa. Sem faixa de audio aqui, o outro lado nunca me ouve, e o
      // unico caminho de volta e o `vigiarAudio`. Por isso registra: silencio
      // sem log e o que torna esse defeito invisivel.
      if (!faixa[papel]) {
        if (papel === 'audio') {
          console.warn('[sala] sender de audio ficou sem faixa ao negociar — o outro lado nao vai me ouvir ate a vigia reanexar');
        }
        return;
      }

      transceiver.sender.replaceTrack(faixa[papel]).catch((err) => {
        console.warn(`[sala] falhou ao anexar a faixa de ${papel}:`, err?.name || err);
      });
    });
  }, []);

  // Reanexa a faixa local de audio num peer que ja esta conectado. E a saida
  // para o caso do sender ter ficado sem faixa: como nao renegociamos, sem
  // isso o silencio so termina com F5.
  const reanexarAudio = useCallback((entry) => {
    const faixa = streamLocalRef.current?.getAudioTracks()[0] || null;
    const sender = entry.senders.audio;
    if (!faixa || !sender) return false;
    sender.replaceTrack(faixa).catch((err) => {
      console.warn('[sala] falhou ao reanexar a faixa de audio:', err?.name || err);
    });
    return true;
  }, []);

  // Confere, depois que a conexao fica pronta, se o audio esta mesmo passando.
  // Sao dois modos de falha diferentes, e so um deles da para consertar daqui:
  //
  // - Nada SAINDO: minha faixa nao esta no sender. O outro nao me ouve, e a
  //   correcao e local — reanexar.
  // - Nada CHEGANDO: o outro lado nao esta mandando. Nao da para consertar
  //   deste lado; fica o registro, senao vira silencio sem explicacao.
  const vigiarAudio = useCallback(async (peerId, pc, entry) => {
    const ler = async () => {
      let saiu = 0;
      let chegou = 0;
      (await pc.getStats()).forEach((r) => {
        if (r.type === 'outbound-rtp' && r.kind === 'audio') saiu += r.bytesSent || 0;
        if (r.type === 'inbound-rtp' && r.kind === 'audio') chegou += r.bytesReceived || 0;
      });
      return { saiu, chegou };
    };

    try {
      const antes = await ler();
      await new Promise((r) => { entry.vigiaEspera = setTimeout(r, JANELA_VIGIA_MS); });
      if (pc.connectionState !== 'connected') return;
      const depois = await ler();

      if (depois.saiu <= antes.saiu) {
        console.warn(`[sala] meu audio nao esta saindo para ${peerId} — reanexando a faixa`);
        reanexarAudio(entry);
      }
      if (depois.chegou <= antes.chegou) {
        console.warn(`[sala] nao esta chegando audio de ${peerId} (conexao ${pc.connectionState})`);
      }
    } catch (_) { /* a vigia nunca pode derrubar a chamada */ }
  }, [reanexarAudio]);

  // `ofertante` decide se criamos as m-lines aqui (quem oferta) ou se deixamos
  // o setRemoteDescription cria-las (quem responde). Ver aplicarMidiaLocal.
  const criarPeer = useCallback((peerId, ofertante) => {
    const existente = peersRef.current.get(peerId);
    if (existente) return existente;

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS, bundlePolicy: 'max-bundle' });

    const entry = {
      pc,
      senders:  { audio: null, camera: null, tela: null },
      streams:  { camera: null, tela: null },
      filaCandidates: [],
      statsTimer: null,
      vigiaTimer: null,
      vigiaEspera: null,
      recuperaTimer: null,
      ofertante: !!ofertante,
      tentativasRestart: 0,
      iceRestartEmAndamento: false,
    };
    peersRef.current.set(peerId, entry);

    if (ofertante) {
      // Ordem fixa: audio, camera, tela (ver PAPEIS).
      pc.addTransceiver('audio', { direction: 'sendrecv' });
      pc.addTransceiver('video', { direction: 'sendrecv' });
      pc.addTransceiver('video', { direction: 'sendrecv' });
      aplicarMidiaLocal(entry);
    }

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      enviar({ tipo: 'sinal', para: peerId, payload: { tipo: 'candidate', candidate: event.candidate } });
    };

    // Os senders vem de addTransceiver, sem associacao a MediaStream, entao
    // `event.streams` chega vazio. Montamos os streams remotos na mao,
    // separando camera e tela pelo indice da m-line (PAPEIS). Um objeto novo
    // a cada faixa faz o React trocar o srcObject do <video>.
    pc.ontrack = (event) => {
      const papel = PAPEIS[pc.getTransceivers().indexOf(event.transceiver)];
      // audio viaja junto do tile de camera para tocar mesmo com a camera off.
      const destino = papel === 'tela' ? 'tela' : 'camera';

      if (!entry.streams[destino]) entry.streams[destino] = new MediaStream();
      const alvo = entry.streams[destino];
      if (!alvo.getTracks().includes(event.track)) alvo.addTrack(event.track);

      atualizarParticipante(peerId, destino === 'tela'
        ? { streamTela: new MediaStream(alvo.getTracks()) }
        : { stream: new MediaStream(alvo.getTracks()) });
    };

    pc.onconnectionstatechange = () => {
      const estado = pc.connectionState;
      setParticipantes((prev) => prev.map((p) => {
        if (p.peerId !== peerId) return p;
        // Repoe os streams SO se tiverem se perdido (ontrack que disparou
        // antes deste peer entrar na lista). Reempurrar um stream que ja esta
        // na tela trocaria o srcObject do <video> a toa e faria piscar.
        const stream = (!p.stream && entry.streams.camera?.getTracks().length)
          ? new MediaStream(entry.streams.camera.getTracks())
          : p.stream;
        const streamTela = (!p.streamTela && entry.streams.tela?.getTracks().length)
          ? new MediaStream(entry.streams.tela.getTracks())
          : p.streamTela;
        return { ...p, conexao: estado, stream, streamTela };
      }));

      if (estado === 'connected') {
        clearTimeout(entry.statsTimer);
        entry.statsTimer = setTimeout(() => medirRota(peerId, pc), DELAY_STATS_MS);
        clearTimeout(entry.vigiaTimer);
        entry.vigiaTimer = setTimeout(() => vigiarAudio(peerId, pc, entry), VIGIA_AUDIO_MS);
        // Voltou a ficar OK: zera o contador para que uma proxima queda tenha
        // suas proprias tres tentativas de restart.
        clearTimeout(entry.recuperaTimer);
        entry.tentativasRestart = 0;
      } else if (estado === 'failed') {
        // `failed` nao se recupera sozinho — restart imediato.
        reiniciarIce(peerId);
      } else if (estado === 'disconnected') {
        // `disconnected` frequentemente volta em 1-2s (jitter de rede). Da uma
        // janela antes de mexer; se persistir, restart.
        clearTimeout(entry.recuperaTimer);
        entry.recuperaTimer = setTimeout(() => {
          const st = pc.connectionState;
          if (st === 'disconnected' || st === 'failed') reiniciarIce(peerId);
        }, ICE_RECUPERACAO_MS);
      }
    };

    return entry;
  }, [atualizarParticipante, enviar, medirRota, aplicarMidiaLocal, vigiarAudio, reiniciarIce]);

  const escoarCandidates = useCallback(async (entry) => {
    while (entry.filaCandidates.length) {
      const candidate = entry.filaCandidates.shift();
      try { await entry.pc.addIceCandidate(candidate); } catch (_) { /* candidato velho */ }
    }
  }, []);

  // Quem CHEGA na sala e quem manda a offer para cada um que ja estava la.
  // Regra fixa e unilateral — com isso os dois lados nunca ofertam ao mesmo
  // tempo e nao precisamos de tratamento de glare.
  const iniciarOffer = useCallback(async (peerId) => {
    const entry = criarPeer(peerId, true);
    try {
      const offer = await entry.pc.createOffer();
      await entry.pc.setLocalDescription(offer);
      enviar({ tipo: 'sinal', para: peerId, payload: { tipo: 'offer', sdp: entry.pc.localDescription } });
    } catch (err) {
      console.error('[sala] falha ao criar offer:', err);
    }
  }, [criarPeer, enviar]);

  const tratarSinal = useCallback(async (de, payload) => {
    const entry = criarPeer(de, false);
    const { pc } = entry;

    try {
      if (payload.tipo === 'offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        // Depois do SRD e antes do createAnswer: e aqui que as nossas faixas
        // entram nos transceivers que a offer acabou de criar. Fora desta
        // janela, a answer sai `recvonly` e nao transmitimos nada.
        aplicarMidiaLocal(entry);
        await escoarCandidates(entry);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        enviar({ tipo: 'sinal', para: de, payload: { tipo: 'answer', sdp: pc.localDescription } });
      } else if (payload.tipo === 'answer') {
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        await escoarCandidates(entry);
      } else if (payload.tipo === 'candidate') {
        // ICE pode chegar antes da descricao remota; nesse caso enfileira.
        if (pc.remoteDescription && pc.remoteDescription.type) {
          await pc.addIceCandidate(payload.candidate);
        } else {
          entry.filaCandidates.push(payload.candidate);
        }
      }
    } catch (err) {
      console.error('[sala] falha ao tratar sinal:', err);
    }
  }, [criarPeer, enviar, escoarCandidates, aplicarMidiaLocal]);

  // --- Midia local ---------------------------------------------------------
  const obterMidia = useCallback(async () => {
    // getUserMedia so existe em contexto seguro: https, ou http em localhost.
    // Acessar o portal por http://192.168.x.x cai aqui.
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(
        'O navegador bloqueou o acesso à câmera/microfone. Abra o portal por HTTPS '
        + '(ou por localhost em desenvolvimento) para usar a sala.',
      );
    }

    try {
      return await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS, video: VIDEO_ALTA });
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
        throw new Error('Permissão de câmera/microfone negada. Libere no cadeado da barra de endereços e tente de novo.');
      }
      // Sem camera (ou camera ocupada por outro app): entra so com audio.
      try {
        const somenteAudio = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
        setAviso('Câmera indisponível — você entrou só com áudio.');
        return somenteAudio;
      } catch (_) {
        throw new Error('Nenhum microfone disponível. Conecte um dispositivo de áudio e tente de novo.');
      }
    }
  }, []);

  // --- Chat ----------------------------------------------------------------
  // O historico e persistente e vem por REST; so as mensagens novas chegam
  // pelo WebSocket. Separar os dois evita mandar o historico inteiro por
  // dentro do canal de sinalizacao.
  const carregarHistorico = useCallback(async (salaId) => {
    setChatCarregando(true);
    try {
      const res = await axios.get(`${API}/sala/${salaId}/mensagens`);
      setMensagens(res.data?.data || []);
    } catch (_) {
      setMensagens([]);
    } finally {
      setChatCarregando(false);
    }
  }, []);

  const enviarMensagem = useCallback((texto) => {
    const limpo = String(texto || '').trim();
    if (!limpo) return;
    enviar({ tipo: 'mensagem', texto: limpo });
  }, [enviar]);

  // Centraliza tudo que precisa acontecer quando uma faixa de camera passa a
  // ser "a nossa": dica de codificacao, respeito ao estado de camera
  // desligada e leitura do que a camera realmente entregou.
  const adotarCamera = useCallback((faixa) => {
    if (!faixa) {
      cameraTrackRef.current = null;
      setConfigCamera(null);
      return;
    }
    // 'motion' diz ao codificador para preservar fluidez em vez de nitidez —
    // e o certo para rosto falando. A tela usa 'detail' (ver alternarTela).
    try { faixa.contentHint = 'motion'; } catch (_) { /* navegador sem suporte */ }
    faixa.enabled = !cameraOffRef.current;
    cameraTrackRef.current = faixa;
    try { setConfigCamera(faixa.getSettings()); } catch (_) { /* ignora */ }
  }, []);

  // Troca microfone ou camera em tempo real. Como os senders ja estao
  // negociados, e so `replaceTrack`: ninguem cai da chamada e nao renegocia.
  const trocarDispositivo = useCallback(async (tipo, deviceId) => {
    const ehCamera = tipo === 'camera';
    const local = streamLocalRef.current;
    if (!local || !deviceId) return;

    let novaFaixa;
    try {
      const nova = await navigator.mediaDevices.getUserMedia(ehCamera
        ? { video: { ...perfilVideoRef.current, deviceId: { exact: deviceId } } }
        : { audio: { ...AUDIO_CONSTRAINTS, deviceId: { exact: deviceId } } });
      novaFaixa = nova.getTracks()[0];
    } catch (_) {
      setErro(`Não foi possível usar ${ehCamera ? 'esta câmera' : 'este microfone'}.`);
      return;
    }
    if (!novaFaixa) return;

    if (ehCamera) adotarCamera(novaFaixa);
    else novaFaixa.enabled = !mudoRef.current;

    const papel = ehCamera ? 'camera' : 'audio';
    for (const entry of peersRef.current.values()) {
      entry.senders[papel]?.replaceTrack(novaFaixa).catch(() => {});
    }

    // Solta a faixa antiga so DEPOIS de trocar nos senders, para nao piscar.
    const antiga = ehCamera ? local.getVideoTracks()[0] : local.getAudioTracks()[0];
    if (antiga) {
      local.removeTrack(antiga);
      antiga.stop();
    }
    local.addTrack(novaFaixa);
    setStreamLocal(new MediaStream(local.getTracks()));

    // Rotulos podem ter mudado (permissao recem-concedida em outro device).
    listarDispositivos();
  }, [adotarCamera, listarDispositivos]);

  // --- Limpeza -------------------------------------------------------------
  const limpar = useCallback(() => {
    for (const peerId of Array.from(peersRef.current.keys())) fecharPeer(peerId);

    streamLocalRef.current?.getTracks().forEach((t) => t.stop());
    streamTelaRef.current?.getTracks().forEach((t) => t.stop());
    streamLocalRef.current = null;
    streamTelaRef.current = null;
    cameraTrackRef.current = null;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      try { wsRef.current.close(); } catch (_) { /* ja fechado */ }
      wsRef.current = null;
    }

    setParticipantes([]);
    setMensagens([]);
    setConfigCamera(null);
    mudoRef.current = false;
    cameraOffRef.current = false;
    setStreamLocal(null);
    setStreamTela(null);
    setSala(null);
    setMeuPeerId(null);
    setTrocandoPara(null);
    substituidoRef.current = false;
    setMudo(false);
    setCameraDesligada(false);
    setCompartilhandoTela(false);
    setAviso('');
  }, [fecharPeer]);

  const sair = useCallback(() => {
    saindoRef.current = true;
    enviar({ tipo: 'sair' });
    limpar();
    setStatus('ocioso');
    setErro('');
    saindoRef.current = false;
  }, [enviar, limpar]);

  // --- Entrada -------------------------------------------------------------
  const entrar = useCallback(async (salaId, token) => {
    if (status === 'conectando' || status === 'na-sala') return;
    setErro('');
    setAviso('');
    setStatus('conectando');

    let midia;
    try {
      midia = await obterMidia();
    } catch (err) {
      setErro(err.message);
      setStatus('erro');
      return;
    }

    streamLocalRef.current = midia;
    adotarCamera(midia.getVideoTracks()[0] || null);
    setStreamLocal(midia);
    listarDispositivos();

    const ws = new WebSocket(wsUrl(token));
    wsRef.current = ws;

    ws.onopen = () => ws.send(JSON.stringify({ tipo: 'entrar', salaId }));

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch (_) { return; }

      switch (msg.tipo) {
        case 'entrou': {
          setMeuPeerId(msg.peerId);
          setSala(msg.sala);
          setTrocandoPara(null);
          setParticipantes(msg.participantes.map(participanteBase));
          setStatus('na-sala');
          setMensagens([]);
          if (msg.sala.chat) carregarHistorico(msg.sala.id);
          msg.participantes.forEach((p) => iniciarOffer(p.peerId));
          break;
        }
        case 'participante-entrou':
          // Quem ja estava so registra e espera a offer do recem-chegado.
          setParticipantes((prev) => [...prev, participanteBase(msg.participante)]);
          break;
        case 'participante-saiu':
          fecharPeer(msg.peerId);
          setParticipantes((prev) => prev.filter((p) => p.peerId !== msg.peerId));
          break;
        case 'participante-estado':
          atualizarParticipante(msg.peerId, {
            mudo:               !!msg.mudo,
            cameraDesligada:    !!msg.cameraDesligada,
            compartilhandoTela: !!msg.compartilhandoTela,
          });
          break;
        case 'mensagem':
          // Chega para todos, inclusive quem enviou — e assim que a mensagem
          // aparece na propria tela, ja com o id do banco.
          setMensagens((prev) => (prev.some((m) => m.id === msg.mensagem.id)
            ? prev
            : [...prev, msg.mensagem]));
          break;
        case 'sinal':
          tratarSinal(msg.de, msg.payload);
          break;
        case 'substituido':
          // Outra aba desta mesma pessoa entrou numa sala. O servidor ja
          // encerrou esta sessao; so precisamos explicar o motivo.
          substituidoRef.current = true;
          setErro('Você entrou na sala em outra aba. Esta sessão foi encerrada.');
          break;
        case 'erro':
          setErro(msg.mensagem);
          // Troca recusada (destino cheio): o servidor nao chegou a nos tirar
          // da sala atual, entao so desfaz o destaque do botao.
          setTrocandoPara(null);
          break;
        default:
          break;
      }
    };

    ws.onclose = (event) => {
      if (saindoRef.current) return;
      const substituido = substituidoRef.current || event.code === 4008;
      substituidoRef.current = false;
      limpar();
      setStatus('erro');
      if (substituido) setErro('Você entrou na sala em outra aba. Esta sessão foi encerrada.');
      else if (event.code === 4003) setErro('Você não tem permissão para usar a sala.');
      else if (event.code === 4001) setErro('Sessão expirada. Faça login de novo.');
      else setErro('Conexão com a sala perdida.');
    };

    ws.onerror = () => { /* o onclose logo em seguida cuida do estado */ };
  }, [status, obterMidia, iniciarOffer, fecharPeer, atualizarParticipante, tratarSinal, limpar, adotarCamera, listarDispositivos, carregarHistorico]);

  // A qualidade acompanha o tamanho da sala: ate `limiteVideoAlta` pessoas vai
  // em 640x360; a partir dai cai para 320x180 + bitrate menor, porque cada
  // navegador passa a codificar um upload a mais por pessoa que entra.
  useEffect(() => {
    if (status !== 'na-sala') return;
    aplicarQualidade(participantes.length + 1);
  }, [status, participantes.length, aplicarQualidade]);

  // Troca de sala pelo MESMO WebSocket: derruba so as conexoes P2P da sala
  // antiga e reaproveita camera/microfone (nao pede permissao de novo, nao
  // pisca o video local). Se a sala destino estiver cheia, o servidor recusa
  // e a gente continua na sala atual.
  const trocarSala = useCallback((salaId) => {
    if (!salaId || salaId === sala?.id || trocandoPara) return;
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;

    for (const peerId of Array.from(peersRef.current.keys())) fecharPeer(peerId);
    setParticipantes([]);
    setTrocandoPara(salaId);
    setErro('');
    enviar({ tipo: 'entrar', salaId });
  }, [sala, trocandoPara, enviar, fecharPeer]);

  // --- Controles -----------------------------------------------------------
  const alternarMudo = useCallback(() => {
    const track = streamLocalRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    const novo = !track.enabled;
    mudoRef.current = novo;
    setMudo(novo);
    enviar({ tipo: 'estado', mudo: novo });
  }, [enviar]);

  // Independente de estar compartilhando tela: sao m-lines separadas, entao
  // desligar a camera nao interfere na apresentacao e vice-versa.
  const alternarCamera = useCallback(() => {
    const track = cameraTrackRef.current;
    if (!track) return;
    track.enabled = !track.enabled;
    const desligada = !track.enabled;
    cameraOffRef.current = desligada;
    setCameraDesligada(desligada);
    enviar({ tipo: 'estado', cameraDesligada: desligada });
  }, [enviar]);

  const pararTela = useCallback(() => {
    const tela = streamTelaRef.current;
    streamTelaRef.current = null;
    setStreamTela(null);
    setCompartilhandoTela(false);

    // Solta so o sender de tela. O de camera nunca foi tocado.
    for (const entry of peersRef.current.values()) {
      entry.senders.tela?.replaceTrack(null).catch(() => {});
    }
    tela?.getTracks().forEach((t) => t.stop());
    enviar({ tipo: 'estado', compartilhandoTela: false });
  }, [enviar]);

  // Compartilhar tela e barato porque a m-line ja existe negociada desde o
  // inicio: e so `replaceTrack` no sender de tela. Camera e tela vao juntas,
  // em faixas separadas — apresentar NAO tira seu rosto da chamada.
  const alternarTela = useCallback(async () => {
    if (streamTelaRef.current) {
      pararTela();
      return;
    }

    if (!navigator.mediaDevices?.getDisplayMedia) {
      setErro('Este navegador não suporta compartilhamento de tela.');
      return;
    }

    let tela;
    try {
      tela = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15, max: 20 } },
        audio: false,
      });
    } catch (_) {
      return; // usuario cancelou o seletor de janela
    }

    const track = tela.getVideoTracks()[0];
    // 'detail' faz o codificador priorizar NITIDEZ sobre fluidez — o certo
    // para slides, planilha e codigo, onde texto borrado inutiliza a sessao.
    try { track.contentHint = 'detail'; } catch (_) { /* navegador sem suporte */ }
    streamTelaRef.current = tela;
    setStreamTela(tela);
    setCompartilhandoTela(true);

    for (const entry of peersRef.current.values()) {
      entry.senders.tela?.replaceTrack(track).catch(() => {});
    }
    enviar({ tipo: 'estado', compartilhandoTela: true });

    // Botao "Parar de compartilhar" do proprio Chrome.
    track.onended = () => pararTela();
  }, [enviar, pararTela]);

  // Plugar/desplugar um fone tem que aparecer na lista na hora.
  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return undefined;
    const aoMudar = () => listarDispositivos();
    navigator.mediaDevices.addEventListener('devicechange', aoMudar);
    return () => navigator.mediaDevices.removeEventListener('devicechange', aoMudar);
  }, [listarDispositivos]);

  // Fechar a aba/navegar para fora nao pode deixar fantasma na sala.
  useEffect(() => {
    const aoFechar = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ tipo: 'sair' }));
      }
    };
    window.addEventListener('beforeunload', aoFechar);
    return () => {
      window.removeEventListener('beforeunload', aoFechar);
      aoFechar();
      limpar();
    };
  }, [limpar]);

  return {
    status,
    sala,
    trocandoPara,
    meuPeerId,
    participantes,
    streamLocal,
    streamTela,
    mudo,
    cameraDesligada,
    compartilhandoTela,
    erro,
    aviso,
    configCamera,
    dispositivos,
    saidaAudio,
    definirSaidaAudio: setSaidaAudio,
    trocarDispositivo,
    mensagens,
    chatCarregando,
    enviarMensagem,
    entrar,
    sair,
    trocarSala,
    alternarMudo,
    alternarCamera,
    alternarTela,
    limparErro: () => setErro(''),
  };
}
